import { AxiosResponse } from 'axios';
import { PersonType } from '@/types/api';
import { supabase } from '@/lib/supabase';
import { asResponse } from '@/lib/supabase-helpers';

export type ChangeReason = 'Promotion' | 'Merit' | 'Adjust' | 'Initial Compensation' | 'Other';

// Map frontend change reason → DB enum value
const changeReasonToDb: Record<string, string> = {
  Promotion: 'promotion',
  Merit: 'merit',
  Adjust: 'adjust',
  'Initial Compensation': 'initial_compensation',
  Other: 'other',
};

const dbToChangeReason: Record<string, ChangeReason> = {
  promotion: 'Promotion',
  merit: 'Merit',
  adjust: 'Adjust',
  initial_compensation: 'Initial Compensation',
  other: 'Other',
};

export type Compensation = {
  changeReason: ChangeReason;
  createdAt: string;
  endDate?: string;
  hourlyRate?: string;
  monthlyPayment?: string;
  id: string;
  person: PersonType;
  personId: string;
  startDate: string;
  updatedAt: string;
};

export type CreateCompensationResponse = Omit<Compensation, 'person'>;

export type CreateCompensationProps = {
  staffMemberId: string;
  hourlyRate?: string;
  monthlyPayment?: string;
  startDate: string;
  endDate?: string | null;
  changeReason: ChangeReason;
};

function mapRowToCompensation(row: any): Omit<Compensation, 'person'> {
  return {
    id: row.id,
    personId: row.profile_id,
    hourlyRate: row.hourly_rate != null ? String(row.hourly_rate) : undefined,
    monthlyPayment: row.monthly_payment != null ? String(row.monthly_payment) : undefined,
    startDate: row.start_date,
    endDate: row.end_date || undefined,
    changeReason: dbToChangeReason[row.change_reason] || 'Other',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapRowToCompensationWithPerson(row: any): Compensation {
  const { profiles, ...rest } = row;
  return {
    ...mapRowToCompensation(rest),
    person: profiles as PersonType,
  };
}

const createCompensation = async ({
  staffMemberId,
  hourlyRate,
  monthlyPayment,
  startDate,
  endDate,
  changeReason,
}: CreateCompensationProps): Promise<AxiosResponse<CreateCompensationResponse>> => {
  const { data, error } = await supabase
    .from('compensation_history')
    .insert({
      profile_id: staffMemberId,
      hourly_rate: hourlyRate ? Number(hourlyRate) : null,
      monthly_payment: monthlyPayment ? Number(monthlyPayment) : null,
      start_date: startDate,
      end_date: endDate || null,
      change_reason: changeReasonToDb[changeReason] || 'other',
    })
    .select()
    .single();

  if (error) throw error;

  return asResponse(mapRowToCompensation(data));
};

const listCompensations = async (
  staffMemberId: string,
): Promise<AxiosResponse<Compensation[]>> => {
  const { data, error } = await supabase
    .from('compensation_history')
    .select('*, profiles(*)')
    .eq('profile_id', staffMemberId)
    .order('start_date', { ascending: false });

  if (error) throw error;

  return asResponse((data || []).map(mapRowToCompensationWithPerson));
};

export type EditCompensationProps = Partial<CreateCompensationProps> & {
  id: string;
};

const editCompensation = async ({
  id,
  staffMemberId,
  hourlyRate,
  monthlyPayment,
  startDate,
  endDate,
  changeReason,
}: EditCompensationProps): Promise<AxiosResponse<CreateCompensationResponse>> => {
  const updates: Record<string, unknown> = {};

  if (staffMemberId !== undefined) updates.profile_id = staffMemberId;
  if (hourlyRate !== undefined) updates.hourly_rate = Number(hourlyRate);
  if (monthlyPayment !== undefined) updates.monthly_payment = Number(monthlyPayment);
  if (startDate !== undefined) updates.start_date = startDate;
  if (endDate !== undefined) updates.end_date = endDate;
  if (changeReason !== undefined) updates.change_reason = changeReasonToDb[changeReason] || 'other';

  const { data, error } = await supabase
    .from('compensation_history')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;

  return asResponse(mapRowToCompensation(data));
};

const compensationService = {
  createCompensation,
  listCompensations,
  editCompensation,
};

export default compensationService;
